import _ from "lodash";
import axiosInstance from "@/config/axiosInstance";
import React, { useEffect, useRef } from "react";
import { getCMSApiRoute } from "../api.util";
import { useSearchParams } from "next/navigation";
import { useSelector } from "react-redux";

export function useCategoryContentList({
  fetchDelay = 0,
  slug,
  initialPage = 1,
  pageSize = 10,
  autoInit = false,
  initialData = [],
  lazyLoading = false,
} = {}) {
  const ageRange = useSelector((state) => state.Auth.ageRange) || 120;
  const [items, setItems] = React.useState(initialData);
  const [hasMore, setHasMore] = React.useState(true);
  const [isLoading, setIsLoading] = React.useState(false);
  const [page, setPage] = React.useState(initialPage);
  const limit = pageSize; // Number of items per page, adjust as necessary
  const init = useRef(false);
  const params = useSearchParams();


  const kind = params.get("kind");

  const loadCategory = async (page) => {
    const controller = new AbortController();
    const { signal } = controller;

    try {
      setIsLoading(true);

      if (page > 1) {
        await new Promise((resolve) => setTimeout(resolve, fetchDelay));
      }

      const response = await axiosInstance.get(getCMSApiRoute("content"), {
        params: {
          pageIndex: limit,
          pageNumber: page,
          category: decodeURIComponent(slug),
          kind: kind || undefined,
        },
        signal,
      });

      if (!response.statusText === "ok") {
        throw new Error("Network response was not ok");
      }

      const { data } = response.data;

      setHasMore(data?.total > page * limit);
      setItems(
        _.uniqBy(
          lazyLoading
            ? [
              ...items,
              ..._.shuffle(data?.results.filter((x) => x.status === 1)),
            ]
            : [...items, ...data?.results.filter((x) => x.status === 1)],
          (x) => x._id
        )
      );
    } catch (error) {
      console.log(error);
      if (error.name === "AbortError") {
        console.log("Fetch aborted");
      } else {
        console.error("There was an error with the fetch operation:", error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (init.current === false && autoInit === true) {
      init.current = true;
      loadCategory(page);
    }
  }, []);

  const onLoadMore = async () => {
    const newOffset = page + 1;

    setPage(newOffset);
    await loadCategory(newOffset);
  };

  return {
    items,
    hasMore,
    isLoading,
    onLoadMore,
  };
}
